import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";

import CursorDataTable from "../../../components/CursorDataTable";
import { useCursorPagination } from "../../../hooks/useCursorPagination";
import { useExcludedStations } from "../../../hooks/useExcludedStations";
import { useStationVisibility } from "../../../hooks/useStationVisibility";
import { getStatusHistory } from "../../../services/stationStatus";
import { EntityType, StatusPeriod } from "../../../types/alerting";
import { formatDuration } from "../../../utils/formatDuration";
import { formatInstant } from "../../../utils/instantFormat";
import { statusStyle } from "../../../utils/statusPresentation";
import AlertMarkers from "./AlertMarkers";
import LocationChargers from "./LocationChargers";
import StationVisibilityTag from "./StationVisibilityTag";

const PAGE_SIZE = 50;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const StationStatusDetailPage = () => {
  const { entityType = "EVSE", entityId = "" } = useParams();
  const navigate = useNavigate();

  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [range, setRange] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });

  const fetchPage = useCallback(
    (cursor: string | null) =>
      getStatusHistory(entityType as EntityType, entityId, {
        from: range.from,
        to: range.to,
        limit: PAGE_SIZE,
        cursor,
      }),
    [entityType, entityId, range],
  );

  const { rows, state, hasMore, loadingMore, error, loadMore } =
    useCursorPagination<StatusPeriod>(fetchPage);

  const current = rows.length > 0 && rows[0].endedAt === null ? rows[0] : null;
  const stationId = entityType === "LOCATION" ? entityId : rows[0]?.locationId ?? "";
  const visibility = useStationVisibility(stationId ? [stationId] : []);
  const excluded = useExcludedStations();

  const applyRange = () =>
    setRange({
      from: from ? startOfDay(from).toISOString() : null,
      to: to ? endOfDay(to).toISOString() : null,
    });

  const clearRange = () => {
    setFrom(null);
    setTo(null);
    setRange({ from: null, to: null });
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Status history</div>
          <div className="text-color-secondary text-sm mt-1">
            {entityType === "LOCATION" ? "Station" : "EVSE"} · {entityId}
          </div>
        </div>
        <Button
          label="All current statuses"
          icon="pi pi-list"
          outlined
          onClick={() => navigate("/alerting/status")}
        />
      </div>

      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <Card title="Current status" className="shadow-1 h-full">
            {current ? (
              <div className="flex flex-column gap-3" data-testid="current-status">
                <div className="flex align-items-center gap-3">
                  <Tag value={current.status} style={statusStyle(current.status)} />
                  <span className="text-color-secondary">
                    for {formatDuration(current.durationSeconds)}
                  </span>
                </div>
                <div className="text-sm text-color-secondary">
                  since {formatInstant(current.startedAt)}
                </div>
                <StationVisibilityTag
                  visibility={visibility[stationId]}
                  excluded={excluded.has(stationId)}
                  withNote
                />
                <AlertMarkers period={current} />
              </div>
            ) : (
              <span className="text-color-secondary" data-testid="no-current-status">
                No open status period — the most recent one has ended.
              </span>
            )}
          </Card>
        </div>

        {entityType === "LOCATION" && (
          <div className="col-12 md:col-6">
            <Card title="EVSEs at this station" className="shadow-1 h-full">
              <LocationChargers locationId={entityId} />
            </Card>
          </div>
        )}
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="from" className="text-sm">From</label>
          <Calendar
            inputId="from"
            value={from}
            onChange={(e) => setFrom((e.value as Date) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="to" className="text-sm">To</label>
          <Calendar
            inputId="to"
            value={to}
            onChange={(e) => setTo((e.value as Date) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>
        <Button label="Apply range" icon="pi pi-filter" onClick={applyRange} />
        <Button label="Clear" icon="pi pi-times" text onClick={clearRange} />
      </div>

      <CursorDataTable<StatusPeriod>
        state={state}
        rows={rows}
        dataKey="startedAt"
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        error={error}
        emptyMessage={`No status periods recorded for ${entityId}.`}
        refusedMessage="Viewing station status requires an administrator entitlement."
        failedMessage="Could not load the status history"
      >
        <Column header="Status" body={(p: StatusPeriod) => <Tag value={p.status} style={statusStyle(p.status)} />} />
        <Column header="Started" body={(p: StatusPeriod) => formatInstant(p.startedAt)} />
        <Column
          header="Ended"
          body={(p: StatusPeriod) =>
            p.endedAt ? formatInstant(p.endedAt) : <span data-testid="open-period">Still open</span>
          }
        />
        <Column header="Duration" body={(p: StatusPeriod) => formatDuration(p.durationSeconds)} />
        <Column header="Alert" body={(p: StatusPeriod) => <AlertMarkers period={p} />} />
      </CursorDataTable>
    </div>
  );
};

export default StationStatusDetailPage;
